import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import {
    FaBolt,
    FaShieldAlt,
    FaTools,
    FaWrench,
    FaIndustry,
    FaHardHat,
    FaDesktop,
    FaBuilding,
    FaPaintBrush,
    FaArrowRight,
    FaStar,
    FaCogs,
    FaBoxes,
    FaEye,
    FaShoppingCart,
    FaClock
} from 'react-icons/fa';

const ProductCategories = () => {
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: true
    });

    // Add navigation hook
    const navigate = useNavigate();

    // Navigation handler functions
    const handleViewProducts = (categoryId) => {
        navigate('/products', { state: { activeCategory: categoryId } });
    };

    const handleGetQuote = () => {
        navigate('/contact');
    };

    const handleBrowseCatalog = () => {
        navigate('/products');
    };

    const handleRequestQuote = () => {
        navigate('/contact');
    };

    const handleCategoryExplore = (categoryId) => {
        navigate('/products', { state: { activeCategory: categoryId } });
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.12,
                duration: 0.6
            }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 40, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    const categories = [
        {
            id: 1,
            title: "Electrical Materials",
            description: "Complete electrical solutions for industrial applications",
            icon: FaBolt,
            image: "/images/electric_material.webp", // Unsplash: electrical panel
            bgImage: "/images/electric_material.webp",
            isMainProduct: true,
            productCount: "2000+",
            items: ["Switches & Sockets", "Power Cables", "Control Panels", "Transformers"],
            color: "blue"
        },
        {
            id: 2,
            title: "Explosion Proof Materials",
            description: "ATEX certified equipment for hazardous environments",
            icon: FaShieldAlt,
            image: "/images/safety_material.webp",
            isMainProduct: false,
            productCount: "500+",
            items: ["Ex-Proof Motors", "Safety Switches", "Gas Detectors", "Emergency Systems"],
            color: "red"
        },
        {
            id: 3,
            title: "Hardware Materials",
            description: "Professional tools and construction fasteners",
            icon: FaTools,
            image: "/images/electrical_installation.webp",
            isMainProduct: false,
            productCount: "800+",
            items: ["Power Tools", "Hand Tools", "Fasteners", "Construction Hardware"],
            color: "gray"
        },
        {
            id: 4,
            title: "Plumbing Materials",
            description: "PVC products and water management systems",
            icon: FaWrench,
            image: "/images/waterproofing_service.webp",
            isMainProduct: false,
            productCount: "600+",
            items: ["PVC Pipes", "Water Pumps", "Valves", "Drainage Systems"],
            color: "cyan"
        },
        {
            id: 5,
            title: "Consumable Materials",
            description: "Essential supplies for daily operations",
            icon: FaBoxes,
            image: "/images/electrician.webp",
            isMainProduct: false,
            productCount: "400+",
            items: ["Adhesives", "Cleaning Supplies", "Lubricants", "Filters"],
            color: "green"
        },
        {
            id: 6,
            title: "PPE Products",
            description: "Personal protective equipment for safety",
            icon: FaHardHat,
            image: "/images/ppe_products.webp",
            isMainProduct: false,
            productCount: "300+",
            items: ["Safety Helmets", "Protective Clothing", "Safety Gloves", "Eye Protection"],
            color: "yellow"
        },
        {
            id: 7,
            title: "Computer Accessories",
            description: "IT equipment and digital peripherals",
            icon: FaDesktop,
            image: "/images/computer_accessories.webp",
            isMainProduct: false,
            productCount: "250+",
            items: ["Keyboards", "Monitors", "Cables", "Storage Devices"],
            color: "purple"
        },
        {
            id: 8,
            title: "Steel Products",
            description: "High-grade steel for construction projects",
            icon: FaBuilding,
            image: "/images/service_background.webp",
            isMainProduct: false,
            productCount: "350+",
            items: ["Structural Steel", "Steel Sheets", "Steel Bars", "Steel Pipes"],
            color: "slate"
        },
        {
            id: 9,
            title: "GI Products",
            description: "Galvanized iron with corrosion resistance",
            icon: FaCogs,
            image: "/images/gi_products.webp",
            isMainProduct: false,
            productCount: "200+",
            items: ["GI Pipes", "GI Sheets", "GI Wire", "GI Conduits"],
            color: "zinc"
        },
        {
            id: 10,
            title: "Paints & Coatings",
            description: "Wall paints and waterproofing solutions",
            icon: FaPaintBrush,
            image: "/images/paint_products.webp",
            isMainProduct: false,
            productCount: "150+",
            items: ["Wall Paints", "Waterproofing", "Primers", "Industrial Coatings"],
            color: "pink"
        }
    ];

    return (
        <section className="py-20 md:py-28 bg-white relative overflow-hidden">
            {/* Light Floating Elements */}
            <div className="absolute inset-0 opacity-30">
                <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
                <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-blue-200/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '4s' }}></div>
            </div>

            <div className="container mx-auto px-4 md:px-8 relative z-10">
                {/* Header */}
                <motion.div
                    ref={ref}
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="text-center mb-16 md:mb-20"
                >
                    <motion.div variants={cardVariants} className="inline-flex items-center gap-2 bg-secondary px-5 py-3 rounded-full mb-8 shadow-lg">
                        <FaIndustry className="text-primary text-base" />
                        <span className="text-primary font-bold text-sm uppercase tracking-wider">
                            AKTP Product Showcase
                        </span>
                    </motion.div>

                    <motion.h2
                        variants={cardVariants}
                        className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 leading-tight"
                    >
                        Industrial Product
                        <span className="block text-secondary drop-shadow-lg">Categories</span>
                    </motion.h2>

                    <motion.p
                        variants={cardVariants}
                        className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
                    >
                        Discover our comprehensive range of 5000+ quality products across 10 specialized categories,
                        backed by 20+ years of industrial expertise
                    </motion.p>
                </motion.div>

                {/* Featured Main Product */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="mb-20"
                >
                    {categories.filter(cat => cat.isMainProduct).map((category) => (
                        <motion.div
                            key={category.id}
                            variants={cardVariants}
                            className="bg-primary rounded-3xl p-8 md:p-12 shadow-2xl"
                        >
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                                {/* Content */}
                                <div className="text-white">
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center shadow-lg">
                                            <category.icon className="text-primary text-2xl" />
                                        </div>
                                        <div className="bg-gradient-to-r from-secondary to-yellow-400 text-primary px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2 shadow-lg">
                                            <FaStar className="text-sm" />
                                            Featured Product
                                        </div>
                                    </div>

                                    <h3 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">{category.title}</h3>
                                    <p className="text-blue-100 text-xl mb-8 leading-relaxed">{category.description}</p>

                                    <div className="grid grid-cols-2 gap-4 mb-10">
                                        {category.items.map((item, idx) => (
                                            <motion.div
                                                key={idx}
                                                className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3"
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: idx * 0.1 + 0.5 }}
                                            >
                                                <div className="w-3 h-3 bg-secondary rounded-full"></div>
                                                <span className="text-blue-100 font-medium">{item}</span>
                                            </motion.div>
                                        ))}
                                    </div>

                                    <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 lg:gap-6">
                                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full lg:w-auto">
                                            <motion.button
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onClick={() => handleViewProducts(category.id)}
                                                className="bg-secondary text-primary px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-base lg:text-lg flex items-center justify-center gap-2 sm:gap-3 shadow-xl hover:bg-yellow-400 transition-all duration-300 w-full sm:w-auto flex-shrink-0 cursor-pointer"
                                            >
                                                <FaEye className="text-sm sm:text-base flex-shrink-0" />
                                                <span className="whitespace-nowrap">View Products</span>
                                            </motion.button>
                                            <motion.button
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onClick={handleGetQuote}
                                                className="border-2 border-white text-white px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-base lg:text-lg hover:bg-white hover:text-primary transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 w-full sm:w-auto flex-shrink-0 cursor-pointer"
                                            >
                                                <FaShoppingCart className="text-sm sm:text-base flex-shrink-0" />
                                                <span className="whitespace-nowrap">Get Quote</span>
                                            </motion.button>
                                        </div>
                                        <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-secondary bg-white/10 backdrop-blur-sm px-4 sm:px-6 py-2 sm:py-3 rounded-xl w-full lg:w-auto text-center lg:text-left flex-shrink-0">
                                            {category.productCount}
                                        </div>
                                    </div>
                                </div>

                                {/* Image - Make clickable */}
                                <div className="relative">
                                    <div
                                        className="relative rounded-3xl overflow-hidden shadow-2xl cursor-pointer hover:scale-105 transition-transform duration-300"
                                        onClick={() => handleViewProducts(category.id)}
                                    >
                                        <img
                                            src={category.image}
                                            alt={category.title}
                                            className="w-full h-80 md:h-96 object-cover"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent"></div>

                                        {/* Hover overlay */}
                                        <div className="absolute inset-0 bg-primary/20 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                            <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                                                <FaArrowRight className="text-white text-2xl" />
                                            </div>
                                        </div>
                                    </div>
                                    {/* Floating Badge */}
                                    <div className="absolute -top-4 -right-4 bg-secondary text-primary px-4 py-2 rounded-full font-bold shadow-lg">
                                        Best Seller
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Other Categories Grid */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                >
                    {categories.filter(cat => !cat.isMainProduct).map((category, index) => (
                        <motion.div
                            key={category.id}
                            variants={cardVariants}
                            whileHover={{
                                y: -8,
                                transition: { duration: 0.3 }
                            }}
                            onClick={() => handleCategoryExplore(category.id)}
                            className="group relative bg-primary rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer"
                        >
                            {/* Image Header */}
                            <div className="relative h-48 overflow-hidden">
                                <img
                                    src={category.image}
                                    alt={category.title}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent"></div>

                                {/* Floating Icon */}
                                <div className="absolute top-4 left-4">
                                    <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center">
                                        <category.icon className="text-white text-lg" />
                                    </div>
                                </div>

                                {/* Product Count */}
                                <div className="absolute top-4 right-4">
                                    <div className="bg-secondary text-primary px-3 py-1 rounded-full text-sm font-bold">
                                        {category.productCount}
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-6 text-white">
                                <h3 className="text-xl font-bold mb-3 group-hover:text-secondary transition-colors duration-300">
                                    {category.title}
                                </h3>
                                <p className="text-gray-300 text-sm mb-5 leading-relaxed">
                                    {category.description}
                                </p>

                                {/* Items */}
                                <div className="space-y-2 mb-6">
                                    {category.items.slice(0, 3).map((item, idx) => (
                                        <div key={idx} className="flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 bg-secondary rounded-full"></div>
                                            <span className="text-gray-300 text-sm">{item}</span>
                                        </div>
                                    ))}
                                    <div className="text-xs text-gray-400 ml-4 opacity-70">
                                        +{category.items.length - 3} more products
                                    </div>
                                </div>

                                {/* CTA */}
                                <div className="flex items-center justify-between">
                                    <motion.div
                                        whileHover={{ x: 5 }}
                                        className="flex items-center gap-2 text-secondary font-semibold text-sm group-hover:gap-3 transition-all duration-300 cursor-pointer"
                                    >
                                        <span>Explore Now</span>
                                        <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
                                    </motion.div>
                                    <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center group-hover:bg-secondary group-hover:scale-110 transition-all duration-300">
                                        <FaArrowRight className="text-white group-hover:text-primary text-sm" />
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Bottom CTA */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="text-center mt-20"
                >
                    <motion.div
                        variants={cardVariants}
                        className="bg-primary rounded-3xl p-10 shadow-2xl"
                    >
                        <h3 className="text-3xl font-bold text-white mb-6">Can't Find What You Need?</h3>
                        <p className="text-gray-300 text-lg mb-6 max-w-2xl mx-auto">
                            Our extensive catalog and expert team can help you find specialized products for any industrial requirement
                        </p>

                        {/* Business Hours Info */}
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-8 inline-block">
                            <div className="flex items-center justify-center gap-3 text-secondary mb-2">
                                <FaClock className="text-lg" />
                                <span className="font-bold">Business Hours</span>
                            </div>
                            <div className="text-gray-300 text-sm">
                                <p>Saturday - Thursday: 8:00 AM - 1:00 PM, 4:00 PM - 8:30 PM</p>
                                <p>Friday: Closed</p>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={handleBrowseCatalog}
                                className="bg-secondary text-primary px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-base lg:text-lg flex items-center justify-center gap-2 sm:gap-3 shadow-xl hover:bg-yellow-400 transition-all duration-300 w-full sm:w-auto flex-shrink-0 cursor-pointer"
                            >
                                <FaIndustry className="text-sm sm:text-base flex-shrink-0" />
                                <span className="whitespace-nowrap">Browse Full Catalog</span>
                                <FaArrowRight className="text-sm sm:text-base flex-shrink-0" />
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={handleRequestQuote}
                                className="border-2 border-white text-white px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-base lg:text-lg hover:bg-white hover:text-primary transition-all duration-300 w-full sm:w-auto flex-shrink-0 cursor-pointer"
                            >
                                <span className="whitespace-nowrap">Request Custom Quote</span>
                            </motion.button>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

export default ProductCategories;